import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { defaultPrompt } from '@goaltree/backend/convex/lib/visionPrompt'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Button } from '@goaltree/ui/components/button'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { isGoalDone } from '@goaltree/ui/lib/goal-groups'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  ChevronDown,
  ImageIcon,
  Loader2,
  Maximize2,
  MoreVertical,
  Play,
  Quote,
  Share,
  Share2,
  Snowflake,
  Sparkles,
  Star,
  Trash2,
  Upload,
  Video,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { GoalSummary } from '@/components/goal-card'
import {
  FreezeGoalDialog,
  GoalFrost,
  goalFreezeState,
  useGoalFreezeActions,
} from '@/components/goal-freeze'
import { MakePublicDialog } from '@/components/make-public-dialog'
import { ShareBoardDialog, type ShareBoardItem } from '@/components/share-board-dialog'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { VisualiseOverlay, type VisualiseSlide } from '@/components/visualise-overlay'
import { useGoals } from '@/hooks/use-goals'
import { useIsPro } from '@/hooks/use-is-pro'
import { CATEGORY_COLORS } from '@/lib/category-color'
import { removeGoal as removeGoalFromLists } from '@/lib/goals-optimistic'
import { goalSlug } from '@/lib/utils'

type BoardRow = {
  goalId: Id<'nodes'>
  kind: 'image' | 'video' | 'upload'
  isVideo: boolean
  status: 'generating' | 'ready' | 'failed'
  prompt: string | null
  error: string | null
  url: string | null
  prevUrl: string | null
}
type Cover = { goalId: string; coverUrl: string | null; videoUrl: string | null }

// Cache the board + covers across remounts so navigating back to the goals
// screen doesn't flash the skeleton (the component remounts on route focus).
let lastKnownBoard: BoardRow[] | null = null
let lastKnownCovers: Cover[] | null = null
// Affirmations are written lazily, once per session at most.
let affirmationsRequested = false

// Keep in sync with AFFIRMATION_MAX in convex/affirmations.ts.
const AFFIRMATION_MAX = 160

// Keep in sync with VIDEO_MONTHLY_LIMIT in convex/visionBoard.ts.
const VIDEO_MONTHLY_LIMIT = 10

export function VisionBoard({
  autoVisualise = false,
  onAutoVisualised,
}: {
  autoVisualise?: boolean
  onAutoVisualised?: () => void
} = {}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { isPro } = useIsPro()
  const goals = useGoals()
  const [freezeId, setFreezeId] = useState<Id<'nodes'> | null>(null)
  const liveBoard = useQuery(api.visionBoard.getBoard) as BoardRow[] | undefined
  const liveCovers = useQuery(api.nodes.getGoalCoverUrls)
  const summaries = useQuery(api.projection.getGoalSummaries)
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { starredGoalIds?: string[] }
    | undefined
  const videoQuota = useQuery(api.visionBoard.getVideoQuota, isPro ? {} : 'skip')
  useEffect(() => {
    if (liveBoard !== undefined) lastKnownBoard = liveBoard
  }, [liveBoard])
  useEffect(() => {
    if (liveCovers !== undefined) lastKnownCovers = liveCovers
  }, [liveCovers])
  const board = liveBoard ?? lastKnownBoard ?? undefined
  const coverUrls = liveCovers ?? lastKnownCovers ?? undefined

  const generateImage = useAction(api.visionBoardActions.generateImage)
  const generateVideo = useAction(api.visionBoardActions.generateVideo)
  const generateAll = useAction(api.visionBoardActions.generateAll)
  const ensureAffirmations = useAction(api.affirmationsNode.ensure)
  const setAffirmation = useMutation(api.affirmations.setAffirmation).withOptimisticUpdate(
    (store, { goalId, text }) => {
      const goals = store.getQuery(api.nodes.getUserGoals, {})
      if (!goals) return
      const affirmation =
        text
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/^["“”'「」]+|["“”'「」]+$/g, '')
          .slice(0, AFFIRMATION_MAX) || undefined
      store.setQuery(
        api.nodes.getUserGoals,
        {},
        goals.map((g) => (g._id === goalId ? { ...g, affirmation } : g)),
      )
    },
  )
  const uploadUrlMut = useMutation(api.visionBoard.uploadUrl)
  const setUpload = useMutation(api.visionBoard.setUpload)
  const removeMedia = useMutation(api.visionBoard.remove).withOptimisticUpdate(
    (store, { goalId }) => {
      const board = store.getQuery(api.visionBoard.getBoard, {})
      const row = board?.find((r) => r.goalId === goalId)
      if (board)
        store.setQuery(
          api.visionBoard.getBoard,
          {},
          board.filter((r) => r.goalId !== goalId),
        )
      const covers = store.getQuery(api.nodes.getGoalCoverUrls, {})
      const cover = covers?.find((c) => c.goalId === goalId)
      if (
        !covers ||
        !cover ||
        (board && row && row.url !== cover.coverUrl && row.url !== cover.videoUrl)
      )
        return
      store.setQuery(
        api.nodes.getGoalCoverUrls,
        {},
        covers.filter((c) => c.goalId !== goalId),
      )
    },
  )
  const removeGoal = useMutation(api.nodes.remove).withOptimisticUpdate((store, { nodeId }) =>
    removeGoalFromLists(store, nodeId),
  )
  const [deleteTarget, setDeleteTarget] = useState<{ id: Id<'nodes'>; title: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  async function commitDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await removeGoal({ nodeId: deleteTarget.id })
      setDeleteTarget(null)
    } catch {
      toast.error(t('common.error'))
    } finally {
      setDeleting(false)
    }
  }

  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [generatingAll, setGeneratingAll] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [lightbox, setLightbox] = useState<{ url: string; video: boolean; title: string } | null>(
    null,
  )
  const [promptEdit, setPromptEdit] = useState<{ goalId: Id<'nodes'>; value: string } | null>(null)
  const [affEdit, setAffEdit] = useState<{ goalId: Id<'nodes'>; value: string } | null>(null)
  const [shareTarget, setShareTarget] = useState<{ id: Id<'nodes'>; slug: string } | null>(null)
  const [visualising, setVisualising] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  const uploadTarget = useRef<Id<'nodes'> | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const starred = new Set(prefs?.starredGoalIds ?? [])
  const goalList = (goals ?? []).filter((g) => !g.isInbox)
  const tiles = [
    ...goalList.filter((g) => starred.has(g._id)),
    ...goalList.filter((g) => !starred.has(g._id)),
  ]
  const byGoal = new Map<string, BoardRow>((board ?? []).map((r) => [r.goalId as string, r]))
  const coverByGoal = new Map<string, Cover>((coverUrls ?? []).map((c) => [c.goalId, c]))
  const summaryByGoal = new Map<string, GoalSummary>(
    (summaries ?? []).map(({ goalId, ...s }) => [goalId as string, s]),
  )

  // The goal cover is the one visual everywhere (generation and uploads mirror
  // onto it); a board row only adds status, prompt and the pre-mirror legacy url.
  function visualOf(id: string) {
    const cover = coverByGoal.get(id)
    const row = byGoal.get(id)
    const busyNow = busy.has(id) || row?.status === 'generating'
    if (cover) return { image: cover.coverUrl, video: cover.videoUrl, busy: busyNow, row }
    const url = row?.url ?? (busyNow ? row?.prevUrl : null) ?? null
    return {
      image: row?.isVideo ? null : url,
      video: row?.isVideo ? url : null,
      busy: busyNow,
      row,
    }
  }
  const hasVisual = (id: string) => {
    const v = visualOf(id)
    return !!(v.image || v.video)
  }
  const pctOf = (g: (typeof tiles)[number]) =>
    g.taskCount > 0 ? Math.round(((g.taskCount - g.remainingCount) / g.taskCount) * 100) : 0
  const withVisual = tiles.filter((g) => hasVisual(g._id)).length
  const missing = tiles.filter((g) => !hasVisual(g._id) && !visualOf(g._id).busy).length

  const needsAffirmation = goals !== undefined && tiles.some((g) => !g.affirmation)
  useEffect(() => {
    if (!needsAffirmation || affirmationsRequested) return
    affirmationsRequested = true
    ensureAffirmations({ language: i18n.language }).catch(() => {})
  }, [needsAffirmation, ensureAffirmations, i18n.language])

  useEffect(() => {
    if (!autoVisualise || goals === undefined || tiles.length === 0) return
    setVisualising(true)
    onAutoVisualised?.()
  }, [autoVisualise, goals, tiles.length, onAutoVisualised])

  function setBusyFor(id: string, on: boolean) {
    setBusy((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }

  async function runImage(goalId: Id<'nodes'>, prompt?: string) {
    const id = goalId as string
    setBusyFor(id, true)
    try {
      await generateImage({ goalId, ...(prompt ? { prompt } : {}) })
    } catch {
      toast.error(t('visionboard.imageError'))
    } finally {
      setBusyFor(id, false)
    }
  }

  async function runVideo(goalId: Id<'nodes'>, prompt?: string) {
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    const id = goalId as string
    setBusyFor(id, true)
    try {
      await generateVideo({ goalId, ...(prompt ? { prompt } : {}) })
    } catch (err) {
      const kind = (err as { data?: { kind?: string } })?.data?.kind
      if (kind === 'videoLimit')
        toast.error(t('visionboard.videoLimit', { count: VIDEO_MONTHLY_LIMIT }))
      else if (kind === 'notPro') setUpgradeOpen(true)
      else toast.error(t('visionboard.videoError'))
    } finally {
      setBusyFor(id, false)
    }
  }

  async function runGenerateAll(kind: 'image' | 'video') {
    if (kind === 'video' && !isPro) {
      setUpgradeOpen(true)
      return
    }
    setGeneratingAll(true)
    try {
      const res = await generateAll({ kind })
      toast.success(t('visionboard.generatedCount', { count: res.generated }))
    } catch {
      toast.error(kind === 'video' ? t('visionboard.videoError') : t('visionboard.imageError'))
    } finally {
      setGeneratingAll(false)
    }
  }

  async function copyShare(slug: string) {
    await navigator.clipboard.writeText(
      withShareUtm(`https://goaltree.ai/share/${slug}`, 'goal_link'),
    )
    toast.success(t('common.shareLinkCopied'))
  }
  function shareGoal(g: { _id: Id<'nodes'>; title: string; isPublic: boolean }) {
    const slug = goalSlug(g.title, g._id as string)
    if (!g.isPublic) setShareTarget({ id: g._id, slug })
    else void copyShare(slug)
  }

  function pickUpload(goalId: Id<'nodes'>) {
    uploadTarget.current = goalId
    fileInputRef.current?.click()
  }

  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    const goalId = uploadTarget.current
    e.target.value = ''
    if (!file || !goalId) return
    const id = goalId as string
    setBusyFor(id, true)
    try {
      const url = await uploadUrlMut()
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      const { storageId } = (await res.json()) as { storageId: string }
      await setUpload({
        goalId,
        storageId: storageId as Id<'_storage'>,
        isVideo: file.type.startsWith('video/'),
      })
    } catch {
      toast.error(t('visionboard.uploadError'))
    } finally {
      setBusyFor(id, false)
    }
  }

  function openGoal(g: { _id: Id<'nodes'>; title: string }) {
    navigate({ to: '/goals/$goalId', params: { goalId: goalSlug(g.title, g._id as string) } })
  }

  const slides: VisualiseSlide[] = [...tiles]
    .sort(
      (a, b) =>
        Number(hasVisual(b._id)) - Number(hasVisual(a._id)) ||
        Number(starred.has(b._id)) - Number(starred.has(a._id)),
    )
    .map((g) => {
      const v = visualOf(g._id)
      return {
        id: g._id,
        title: g.title,
        category: g.category,
        pct: pctOf(g),
        affirmation: g.affirmation,
        imageUrl: v.image,
        videoUrl: v.video,
      }
    })

  const shareItems: ShareBoardItem[] = tiles.map((g) => ({
    id: g._id,
    title: g.title,
    category: g.category,
    imageUrl: visualOf(g._id).image ?? null,
    isPublic: g.isPublic,
    done: isGoalDone(g),
  }))

  const toolBtn =
    'flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-border px-3.5 text-[13px] font-bold transition-colors hover:bg-muted disabled:opacity-60'

  return (
    <div>
      {tiles.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setVisualising(true)}
            className="flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 text-[13px] font-bold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Play className="h-3.5 w-3.5 fill-current" />
            {t('goalsBoard.visualiseMin')}
          </button>
          <div className="flex h-9 shrink-0 overflow-hidden rounded-full border border-border">
            <button
              type="button"
              onClick={() => runGenerateAll('image')}
              disabled={generatingAll || missing === 0}
              className="flex cursor-pointer items-center gap-1.5 pl-3.5 pr-2.5 text-[13px] font-bold transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-60"
            >
              {generatingAll ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {t('goalsBoard.generateMissing', { count: missing })}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    type="button"
                    disabled={generatingAll || missing === 0}
                    aria-label={t('visionboard.generateAll')}
                    className="flex w-8 cursor-pointer items-center justify-center border-l border-border transition-colors hover:bg-muted disabled:opacity-60"
                  >
                    <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>
                }
              />
              <DropdownMenuContent align="end" className="w-max min-w-0">
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => runGenerateAll('image')}
                >
                  <ImageIcon className="h-4 w-4" />
                  {t('goalsBoard.missingAsImages', { count: missing })}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => runGenerateAll('video')}
                >
                  <Video className="h-4 w-4" />
                  {t('goalsBoard.missingAsVideos', { count: missing })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button type="button" onClick={() => setShareOpen(true)} className={toolBtn}>
            <Share className="h-3.5 w-3.5" />
            {t('goalsBoard.shareBoard')}
          </button>
          <p className="w-full text-xs text-muted-foreground sm:ml-auto sm:w-auto">
            {t('goalsBoard.withVisuals', { done: withVisual, total: tiles.length })}
            {isPro && videoQuota && (
              <> · {t('goalsBoard.videosLeft', { count: videoQuota.remaining })}</>
            )}
          </p>
        </div>
      )}

      {board === undefined || goals === undefined ? (
        <div className="@container">
          <div className="grid auto-rows-[8rem] grid-flow-dense grid-cols-2 gap-2 @xl:auto-rows-[9rem] @xl:grid-cols-3 @xl:gap-3 @4xl:grid-cols-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={cn(
                  'animate-pulse rounded-2xl bg-muted/30',
                  i === 0 ? 'col-span-2 row-span-2' : i % 3 === 1 && 'row-span-2',
                )}
              />
            ))}
          </div>
        </div>
      ) : tiles.length === 0 ? (
        <div className="py-24 text-center">
          <p className="text-xl font-bold text-foreground">{t('visionboard.empty')}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t('visionboard.emptyDesc')}</p>
        </div>
      ) : (
        // Masonry: starred goals take 2×2, others alternate 1×1 and 1×2 so the
        // board reads like a pinboard; dense flow back-fills the gaps. Columns
        // follow the board's own width (2 → 3 → 4), not the viewport, since the
        // sidebar eats into it on desktop.
        <div className="@container">
          <div className="grid auto-rows-[8rem] grid-flow-dense grid-cols-2 gap-2 @xl:auto-rows-[9rem] @xl:grid-cols-3 @xl:gap-3 @4xl:grid-cols-4">
            {tiles.map((g, i) => {
              const id = g._id as string
              const v = visualOf(id)
              const isStar = starred.has(id)
              const done = isGoalDone(g)
              const pct = pctOf(g)
              const summary = summaryByGoal.get(id)
              const media = v.image || v.video
              const prompt = v.row?.prompt ?? defaultPrompt(g.title, g.category)
              const color = CATEGORY_COLORS[g.category.toLowerCase()] ?? '#6b7280'
              const tall = !media || i % 3 === 1
              const { frozen, until: frozenUntil } = goalFreezeState(g.freezes)
              return (
                <div
                  key={id}
                  className={cn(
                    'group relative overflow-hidden rounded-2xl border',
                    isStar ? 'col-span-2 row-span-2' : tall && 'row-span-2',
                    media ? 'border-border/50 bg-muted/20' : 'border-dashed border-border bg-card',
                  )}
                >
                  {media ? (
                    <button
                      type="button"
                      onClick={() => openGoal(g)}
                      className="absolute inset-0 h-full w-full cursor-pointer"
                      aria-label={g.title}
                    >
                      {v.video ? (
                        <video
                          src={v.video}
                          poster={v.image ?? undefined}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={v.image!}
                          alt={g.title}
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                      )}
                      <span className="absolute inset-0 bg-gradient-to-b from-transparent from-35% to-black/80" />
                    </button>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 p-3 pb-14 text-center">
                      <p
                        className="line-clamp-4 max-w-[26ch] text-[11px] italic leading-snug text-muted-foreground"
                        title={prompt}
                      >
                        “{prompt}”
                      </p>
                      <div className="flex flex-wrap justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => runImage(g._id, v.row?.prompt ?? undefined)}
                          disabled={v.busy}
                          className="flex cursor-pointer items-center gap-1 rounded-full border border-primary/45 bg-primary/12 px-2.5 py-1 text-[11px] font-bold text-primary transition-colors hover:bg-primary/20"
                        >
                          <Sparkles className="h-3 w-3" />
                          {t('visionboard.generate')}
                        </button>
                        <button
                          type="button"
                          onClick={() => pickUpload(g._id)}
                          disabled={v.busy}
                          className="cursor-pointer rounded-full border border-border px-2.5 py-1 text-[11px] font-bold transition-colors hover:bg-muted"
                        >
                          {t('visionboard.upload')}
                        </button>
                      </div>
                    </div>
                  )}

                  {v.busy && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-[1px]">
                      <Loader2 className="h-7 w-7 animate-spin text-foreground/70" />
                    </div>
                  )}

                  {v.row?.status === 'failed' && !v.busy && (
                    <div className="absolute left-2 top-2 rounded-full bg-destructive/90 px-2 py-0.5 text-[10px] font-semibold text-white">
                      {t('visionboard.failed')}
                    </div>
                  )}

                  {v.video && !v.busy && v.row?.status !== 'failed' && (
                    <span className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/50 px-1.5 py-px text-[9px] font-extrabold tracking-wide text-white transition-opacity [@media(hover:hover)]:group-hover:opacity-0">
                      ▶ {t('goalsBoard.videoBadge')}
                    </span>
                  )}

                  {media && !v.busy && (
                    <button
                      type="button"
                      onClick={() =>
                        setLightbox({
                          url: (v.video ?? v.image)!,
                          video: !!v.video,
                          title: g.title,
                        })
                      }
                      aria-label={t('visionboard.fullscreen')}
                      title={t('visionboard.fullscreen')}
                      className="absolute left-1.5 top-1.5 hidden h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-black/40 text-white/90 opacity-0 backdrop-blur-sm transition-opacity hover:bg-black/60 group-hover:opacity-100 [@media(hover:hover)]:flex"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </button>
                  )}

                  {/* Media tiles open via the full-tile button; empty tiles have
                      Generate/Upload in the middle, so their title opens the goal. */}
                  <div
                    {...(!media && {
                      role: 'button',
                      tabIndex: 0,
                      onClick: () => openGoal(g),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          openGoal(g)
                        }
                      },
                    })}
                    className={cn(
                      'absolute inset-x-0 bottom-0 p-2.5',
                      isStar && '@xl:p-3.5',
                      media
                        ? 'pointer-events-none text-white'
                        : 'cursor-pointer rounded-b-2xl text-foreground transition-colors hover:bg-muted/40',
                    )}
                  >
                    {g.affirmation && media && (
                      <p
                        className={cn(
                          'mb-1 hidden italic leading-snug text-white/90 [@media(hover:hover)]:group-hover:line-clamp-3',
                          isStar ? 'text-sm' : 'text-[11px]',
                        )}
                      >
                        “{g.affirmation}”
                      </p>
                    )}
                    <p
                      className={cn(
                        'line-clamp-2 font-bold leading-tight',
                        isStar ? 'text-[15px] @xl:text-lg' : 'text-[13px]',
                      )}
                    >
                      {g.title}
                      {isStar && (
                        <Star className="ml-1 inline h-3 w-3 -translate-y-px fill-yellow-400 text-yellow-400" />
                      )}
                    </p>
                    <p
                      className={cn(
                        'mt-0.5 truncate text-[10.5px]',
                        media ? 'text-white/75' : 'text-muted-foreground',
                      )}
                    >
                      {done
                        ? `✓ ${t('goals.done')}`
                        : [
                            summary?.milestone &&
                              t('goalsBoard.milestoneShort', {
                                n: summary.milestone.index + 1,
                                total: summary.milestone.count,
                              }),
                            isStar ? summary?.milestone?.title : null,
                            `${pct}%`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                    <div
                      className={cn(
                        'mt-1.5 h-[3px] overflow-hidden rounded-full',
                        media ? 'bg-white/25' : 'bg-muted',
                      )}
                    >
                      <div
                        className="h-full transition-[width] duration-500"
                        style={{
                          width: `${done ? 100 : pct}%`,
                          backgroundColor: media ? '#fff' : color,
                        }}
                      />
                    </div>
                  </div>

                  <GoalFrost
                    goalId={id}
                    frozen={frozen}
                    until={frozenUntil}
                    rounded="rounded-2xl"
                    pillClassName="top-2 left-2"
                  />
                  <div className="absolute right-1.5 top-1.5 z-20 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-within:opacity-100">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <button
                            type="button"
                            aria-label={t('common.more', { defaultValue: 'More' })}
                            className={cn(
                              'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full backdrop-blur transition-colors',
                              media
                                ? 'bg-black/40 text-white hover:bg-black/60'
                                : 'bg-muted text-foreground hover:bg-muted/70',
                            )}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        }
                      />
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem className="cursor-pointer" onClick={() => shareGoal(g)}>
                          <Share2 className="h-4 w-4" />
                          {t('shareModal.share')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => runImage(g._id, v.row?.prompt ?? undefined)}
                        >
                          <ImageIcon className="h-4 w-4" />
                          {t('visionboard.generateImage')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => runVideo(g._id, v.row?.prompt ?? undefined)}
                        >
                          <Video className="h-4 w-4" />
                          {t('visionboard.generateVideo')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => pickUpload(g._id)}
                        >
                          <Upload className="h-4 w-4" />
                          {t('visionboard.upload')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() =>
                            setPromptEdit({ goalId: g._id, value: v.row?.prompt ?? '' })
                          }
                        >
                          <Sparkles className="h-4 w-4" />
                          {t('visionboard.editPrompt')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => setAffEdit({ goalId: g._id, value: g.affirmation ?? '' })}
                        >
                          <Quote className="h-4 w-4" />
                          {t('goalsBoard.editAffirmation')}
                        </DropdownMenuItem>
                        {(v.row || media) && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            variant="destructive"
                            onClick={() => removeMedia({ goalId: g._id })}
                          >
                            <Trash2 className="h-4 w-4" />
                            {t('visionboard.remove')}
                          </DropdownMenuItem>
                        )}
                        {!done && (
                          <FreezeMenuItem
                            goalId={g._id}
                            frozen={frozen}
                            onFreeze={() => setFreezeId(g._id)}
                          />
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="cursor-pointer"
                          variant="destructive"
                          onClick={() => setDeleteTarget({ id: g._id, title: g.title })}
                        >
                          <Trash2 className="h-4 w-4" />
                          {t('goalsBoard.deleteGoal')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={onFileChosen}
      />

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl overflow-hidden p-0">
          <DialogTitle className="sr-only">{lightbox?.title ?? ''}</DialogTitle>
          {lightbox &&
            (lightbox.video ? (
              <video src={lightbox.url} controls autoPlay loop className="w-full" />
            ) : (
              <img src={lightbox.url} alt={lightbox.title} decoding="async" className="w-full" />
            ))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!promptEdit} onOpenChange={(o) => !o && setPromptEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogTitle>{t('visionboard.editPrompt')}</DialogTitle>
          <textarea
            value={promptEdit?.value ?? ''}
            onChange={(e) => setPromptEdit((p) => (p ? { ...p, value: e.target.value } : p))}
            rows={4}
            placeholder={t('visionboard.promptPlaceholder')}
            className="w-full resize-none rounded-lg border border-border bg-background p-3 text-sm outline-none focus:border-primary"
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              onClick={() => {
                if (!promptEdit) return
                const { goalId, value } = promptEdit
                setPromptEdit(null)
                runImage(goalId, value)
              }}
            >
              <ImageIcon className="h-4 w-4" />
              {t('visionboard.generateImage')}
            </Button>
            <Button
              onClick={() => {
                if (!promptEdit) return
                const { goalId, value } = promptEdit
                setPromptEdit(null)
                runVideo(goalId, value)
              }}
            >
              <Video className="h-4 w-4" />
              {t('visionboard.generateVideo')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!affEdit} onOpenChange={(o) => !o && setAffEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogTitle>{t('goalsBoard.editAffirmation')}</DialogTitle>
          <textarea
            value={affEdit?.value ?? ''}
            onChange={(e) => setAffEdit((p) => (p ? { ...p, value: e.target.value } : p))}
            rows={3}
            maxLength={160}
            placeholder={t('goalsBoard.affirmationPlaceholder')}
            className="w-full resize-none rounded-lg border border-border bg-background p-3 text-sm italic outline-none focus:border-primary"
          />
          <div className="flex justify-end">
            <Button
              onClick={() => {
                if (!affEdit) return
                const { goalId, value } = affEdit
                setAffEdit(null)
                setAffirmation({ goalId, text: value }).catch(() => toast.error(t('common.error')))
              }}
            >
              {t('common.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {visualising && slides.length > 0 && (
        <VisualiseOverlay slides={slides} onClose={() => setVisualising(false)} />
      )}
      <ShareBoardDialog open={shareOpen} onOpenChange={setShareOpen} items={shareItems} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="vision-video" />
      {freezeId && (
        <FreezeGoalDialog
          goalId={freezeId}
          open
          onOpenChange={(open) => !open && setFreezeId(null)}
        />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sidebar.deleteGoalTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sidebar.deleteGoalDesc', { title: deleteTarget?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={commitDelete} disabled={deleting}>
              {deleting ? t('sidebar.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {shareTarget && (
        <MakePublicDialog
          open={!!shareTarget}
          onOpenChange={(o) => !o && setShareTarget(null)}
          goalId={shareTarget.id}
          onMadePublic={() => copyShare(shareTarget.slug)}
        />
      )}
    </div>
  )
}

function FreezeMenuItem({
  goalId,
  frozen,
  onFreeze,
}: {
  goalId: Id<'nodes'>
  frozen: boolean
  onFreeze: () => void
}) {
  const { t } = useTranslation()
  const { unfreeze } = useGoalFreezeActions(goalId)
  return (
    <DropdownMenuItem
      className="cursor-pointer"
      onClick={() => (frozen ? void unfreeze() : onFreeze())}
    >
      <Snowflake className="h-4 w-4 text-sky-500" />
      {t(frozen ? 'goalFreeze.unfreeze' : 'goalFreeze.freeze')}
    </DropdownMenuItem>
  )
}
